import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  AppBar,
  Button,
  FormControlLabel,
  IconButton,
  makeStyles,
  Switch,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
  CircularProgress
} from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import FlowCanvas from "./FlowCanvas";
import FlowNodePalette from "./FlowNodePalette";
import FlowNodeConfig from "./FlowNodeConfig";
import FlowTriggersPanel from "./FlowTriggersPanel";
import {
  buildDefaultGraph,
  graphToFlowState,
  flowStateToGraph
} from "./nodeTypes";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 64px)",
    background: theme.palette.background.default
  },
  toolbar: {
    minHeight: 56,
    gap: theme.spacing(1)
  },
  nameField: {
    minWidth: 220,
    background: theme.palette.background.paper,
    borderRadius: 4
  },
  grow: { flexGrow: 1 },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40
  },
  body: {
    flex: 1,
    display: "flex",
    minHeight: 0,
    overflow: "hidden"
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1
  }
}));

const ChatFlowEditor = () => {
  const classes = useStyles();
  const history = useHistory();
  const { chatFlowId } = useParams();
  const isNew = chatFlowId === "new";
  const { whatsApps } = useWhatsApps();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [name, setName] = useState("Novo fluxo");
  const [active, setActive] = useState(false);
  const [triggerType, setTriggerType] = useState("any");
  const [triggerKeywords, setTriggerKeywords] = useState([]);
  const [whatsappId, setWhatsappId] = useState(null);
  const [triggerOncePerChat, setTriggerOncePerChat] = useState(true);
  const [triggersEnabled, setTriggersEnabled] = useState(true);
  const initial = useMemo(() => graphToFlowState(buildDefaultGraph()), []);
  const [nodes, setNodes] = useState(initial.nodes);
  const [edges, setEdges] = useState(initial.edges);
  const [selectedId, setSelectedId] = useState(null);

  const selectedNode = useMemo(
    () => nodes.find(n => n.id === selectedId) || null,
    [nodes, selectedId]
  );

  const loadFlow = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/chat-flows/${chatFlowId}`);
      setName(data.name || "");
      setActive(Boolean(data.active));
      const t = String(data.triggerType || "").toLowerCase();
      if (t === "exact" || t === "keyword" || t === "any") {
        setTriggerType(t);
      } else if ((data.keyword || "").trim()) {
        setTriggerType("keyword");
      } else {
        setTriggerType("any");
      }
      const kws = Array.isArray(data.triggerKeywords)
        ? data.triggerKeywords
        : (data.keyword || "").trim()
        ? [data.keyword.trim()]
        : [];
      setTriggerKeywords(kws);
      setWhatsappId(data.whatsappId != null ? data.whatsappId : null);
      setTriggerOncePerChat(data.triggerOncePerChat !== false);
      setTriggersEnabled(data.triggersEnabled !== false);
      const state = graphToFlowState(data.graph || buildDefaultGraph());
      setNodes(state.nodes);
      setEdges(state.edges);
    } catch (err) {
      toastError(err);
      history.push("/chat-flows");
    } finally {
      setLoading(false);
    }
  }, [chatFlowId, history, isNew]);

  useEffect(() => {
    loadFlow();
  }, [loadFlow]);

  const handleNodeDataChange = data => {
    if (!selectedId) return;
    setNodes(nds =>
      nds.map(n => (n.id === selectedId ? { ...n, data } : n))
    );
  };

  const handleDeleteNode = () => {
    if (!selectedId || selectedId === "start") return;
    setNodes(nds => nds.filter(n => n.id !== selectedId));
    setEdges(eds =>
      eds.filter(e => e.source !== selectedId && e.target !== selectedId)
    );
    setSelectedId(null);
  };

  const handleSave = async () => {
    const nameValue = (name || "").trim();
    if (nameValue.length < 2) {
      toast.error(i18n.t("chatFlows.editor.nameRequired"));
      return;
    }
    if (
      triggersEnabled !== false &&
      (triggerType === "keyword" || triggerType === "exact") &&
      !(triggerKeywords || []).length
    ) {
      toast.error(i18n.t("chatFlows.triggers.phrasesRequired"));
      setTab(1);
      return;
    }
    setSaving(true);
    try {
      const graph = flowStateToGraph(nodes, edges);
      const payload = {
        name: nameValue,
        active,
        triggerType,
        triggerKeywords:
          triggerType === "any" ? null : triggerKeywords || [],
        keyword:
          triggerType === "any"
            ? null
            : (triggerKeywords && triggerKeywords[0]) || null,
        whatsappId: whatsappId == null ? null : whatsappId,
        triggerOncePerChat: Boolean(triggerOncePerChat),
        triggersEnabled: triggersEnabled !== false,
        graph
      };
      if (isNew) {
        const { data } = await api.post("/chat-flows", payload);
        toast.success(i18n.t("chatFlows.toasts.created"));
        history.replace(`/chat-flows/${data.id}`);
      } else {
        await api.put(`/chat-flows/${chatFlowId}`, payload);
        toast.success(i18n.t("chatFlows.toasts.updated"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar className={classes.toolbar}>
          <IconButton edge="start" onClick={() => history.push("/chat-flows")}>
            <ArrowBack />
          </IconButton>
          <Typography variant="subtitle1">
            {i18n.t("chatFlows.editor.title")}
          </Typography>
          <TextField
            className={classes.nameField}
            size="small"
            variant="outlined"
            label={i18n.t("chatFlows.form.name")}
            value={name}
            onChange={e => {
              const value = e.target.value;
              setName(value);
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={e => {
                  const checked = e.target.checked;
                  setActive(checked);
                }}
                color="primary"
              />
            }
            label={i18n.t("chatFlows.form.active")}
          />
          <div className={classes.grow} />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {i18n.t("chatFlows.buttons.save")}
          </Button>
        </Toolbar>
        <Tabs
          className={classes.tabs}
          value={tab}
          onChange={(_e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={i18n.t("chatFlows.editor.tabFlow")} />
          <Tab label={i18n.t("chatFlows.editor.tabTriggers")} />
        </Tabs>
      </AppBar>
      {tab === 0 ? (
        <div className={classes.body}>
          <FlowNodePalette />
          <FlowCanvas
            nodes={nodes}
            setNodes={setNodes}
            edges={edges}
            setEdges={setEdges}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          <FlowNodeConfig
            node={selectedNode}
            onChange={handleNodeDataChange}
            onDelete={handleDeleteNode}
          />
        </div>
      ) : (
        <div className={classes.body}>
          <FlowTriggersPanel
            triggerType={triggerType}
            setTriggerType={setTriggerType}
            triggerKeywords={triggerKeywords}
            setTriggerKeywords={setTriggerKeywords}
            whatsappId={whatsappId}
            setWhatsappId={setWhatsappId}
            whatsapps={whatsApps || []}
            triggerOncePerChat={triggerOncePerChat}
            setTriggerOncePerChat={setTriggerOncePerChat}
            triggersEnabled={triggersEnabled}
            setTriggersEnabled={setTriggersEnabled}
          />
        </div>
      )}
    </div>
  );
};

export default ChatFlowEditor;
